import { useState, useMemo, useEffect } from 'react';
import { Pencil, Trash2, ChevronDown, ChevronUp, Folder, CheckCircle, Clock, FolderOpen, Calendar, Archive, User, Check } from 'lucide-react';
import { Matter, Goal, Task, Project, Client, db } from '../../lib/db';
import { MatterDetailModalFullScreen } from './MatterDetailModalFullScreen';

interface MatterCardProps {
  matter: Matter;
  goals: Goal[];
  tasks?: Task[];
  projects?: Project[];
  clients?: Client[];
  userId: string;
  onEdit: (matter: Matter) => void;
  onDelete: (matter: Matter) => void;
  onArchive: (matter: Matter) => void;
  onMarkComplete?: (matter: Matter) => void;
  onMarkIncomplete?: (matter: Matter) => void;
  onDragStart: (e: React.DragEvent, matter: Matter) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, matter: Matter) => void;
  isArchiveView?: boolean;
  shouldOpen?: boolean;
  onOpened?: () => void;
}

export function MatterCard({
  matter,
  goals,
  tasks,
  projects,
  clients,
  userId,
  onEdit,
  onDelete,
  onArchive,
  onMarkComplete,
  onMarkIncomplete,
  onDragStart,
  onDragOver,
  onDrop,
  isArchiveView = false,
  shouldOpen = false,
  onOpened,
}: MatterCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [canArchive, setCanArchive] = useState(false);
  const [checking, setChecking] = useState(false);
  const associatedGoals = goals.filter(goal => goal.matter_id === matter.id);
  const goalCount = associatedGoals.length;
  const activeGoalCount = associatedGoals.filter(g => g.status !== 'Archived' && g.status !== 'Complete').length;
  
  const statistics = useMemo(() => {
    if (!tasks) return { totalTasks: 0, completedTasks: 0, totalTime: 0, allDeliverablesComplete: false };
    
    const goalIds = associatedGoals.map(g => g.id);
    const matterTasks = tasks.filter(task => task.goal_id && goalIds.includes(task.goal_id));
    const completedTasks = matterTasks.filter(task => task.status === 'Complete').length;
    
    // Check if all deliverables are complete (have all their tasks completed)
    const allDeliverablesComplete = associatedGoals.length > 0 && associatedGoals.every(goal => {
      const goalTasks = tasks.filter(task => task.goal_id === goal.id);
      return goalTasks.length > 0 && goalTasks.every(task => task.status === 'Complete');
    });
    
    // Calculate total time (this is a placeholder - would need time entries)
    const totalTime = 0; // TODO: Calculate from time entries
    
    return {
      totalTasks: matterTasks.length,
      completedTasks,
      totalTime,
      allDeliverablesComplete,
    };
  }, [tasks, associatedGoals]);

  useEffect(() => {
    if (shouldOpen) {
      setShowDetailModal(true);
      onOpened?.();
    }
  }, [shouldOpen, onOpened]);

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${matter.title}"?`)) {
      onDelete(matter);
    }
  };

  const handleArchive = async () => {
    if (isArchiveView) {
      if (window.confirm(`Are you sure you want to unarchive "${matter.title}"?`)) {
        onArchive(matter);
      }
    } else if (activeGoalCount === 0) {
      if (window.confirm(`Are you sure you want to archive "${matter.title}"?`)) {
        onArchive(matter);
      }
    }
  };

  const getStatusColor = () => {
    switch (matter.status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <div
        className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
        draggable
        onDragStart={(e) => onDragStart(e, matter)}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, matter)}
        onClick={(e) => {
          // Don't open modal if clicking on buttons or dragging
          if (!(e.target as HTMLElement).closest('button')) {
            setShowDetailModal(true);
          }
        }}
      >
        <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <Folder className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">{matter.title}</h3>
            {matter.is_complete && (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
              {matter.status}
            </span>
          </div>
          {matter.description && (
            <p className="mt-1 text-sm text-gray-600">{matter.description}</p>
          )}
          {matter.project_id && projects && (
            <div className="mt-1 flex items-center text-sm text-purple-600">
              <FolderOpen className="h-4 w-4 mr-1" />
              {projects.find(p => p.id === matter.project_id)?.title || 'Unknown Project'}
            </div>
          )}
          {matter.client_id && clients && (
            <div className="mt-1 flex items-center text-sm text-indigo-600">
              <User className="h-4 w-4 mr-1" />
              {clients.find(c => c.id === matter.client_id)?.name || 'Unknown Client'}
            </div>
          )}
          <div className="mt-2 flex items-center flex-wrap gap-2 text-sm text-gray-500">
            {matter.start_date && (
              <span className="flex items-center text-green-600">
                <Calendar className="h-4 w-4 mr-1" />
                Start: {formatDate(matter.start_date)}
              </span>
            )}
            {matter.deadline && (
              <span className="flex items-center text-red-600">
                <Calendar className="h-4 w-4 mr-1" />
                Due: {formatDate(matter.deadline)}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
            <span>{goalCount} deliverable{goalCount !== 1 ? 's' : ''}</span>
            {tasks && statistics.totalTasks > 0 && (
              <>
                <span className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {statistics.completedTasks}/{statistics.totalTasks} tasks
                </span>
                {statistics.totalTime > 0 && (
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {Math.round(statistics.totalTime / 60)}h
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => onEdit(matter)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Edit matter"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {statistics.allDeliverablesComplete && !matter.is_complete && onMarkComplete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkComplete(matter);
              }}
              className="p-1 text-gray-400 hover:text-green-600"
              title="Mark Matter Complete"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          {matter.is_complete && onMarkIncomplete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkIncomplete(matter);
              }}
              className="p-1 text-green-600 hover:text-gray-600"
              title="Mark Matter Incomplete"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          {(isArchiveView || (activeGoalCount === 0 && !matter.is_archived)) && (
            <button
              onClick={handleArchive}
              className="p-1 text-gray-400 hover:text-blue-600"
              title={isArchiveView ? "Unarchive matter" : "Archive matter"}
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Delete matter"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isExpanded && goalCount > 0 && (
        <div className="mt-4 space-y-2 border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700">Associated Deliverables:</h4>
          <ul className="space-y-1">
            {associatedGoals.map(goal => (
              <li key={goal.id} className="text-sm text-gray-600 pl-4">
                • {goal.title}
                {goal.category && (
                  <span className="ml-2 text-xs text-gray-500">({goal.category})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
    
    <MatterDetailModalFullScreen
      matter={matter}
      goals={goals}
      tasks={tasks}
      isOpen={showDetailModal}
      onClose={() => setShowDetailModal(false)}
      onEdit={onEdit}
      userId={userId}
    />
    </>
  );
}